import PropTypes from "prop-types";
import classNames from "classnames";
import {useEffect, useMemo, useRef, useState} from "react";
import {CardElement} from "@stripe/react-stripe-js";

const DEFAULT_OPTIONS = {hidePostalCode: true};

export default function WebsiteCardElement({
    error,
    errorClassName,
    errorMessage,
    id = "card_details",
    label = "Card Information",
    name = "card_details",
    onChange,
    options = DEFAULT_OPTIONS,
    pageRef,
    required = false
}) {
    const containerRef = useRef(null);
    const [focused, setFocused] = useState(false);
    const [themeStyle, setThemeStyle] = useState({
        fontSize: "18px",
        fontSmoothing: "antialiased"
    });

    useEffect(() => {
        const themeRoot = pageRef?.current || containerRef.current?.closest(".preview") || document.documentElement;
        const styles = getComputedStyle(themeRoot);
        const colour = styles.getPropertyValue("--text-colour").trim();
        const font = styles.getPropertyValue("--base-font").trim();

        setThemeStyle({
            ...(colour ? {color: colour} : {}),
            ...(font ? {fontFamily: font} : {}),
            fontSize: "18px",
            fontSmoothing: "antialiased"
        });
    }, [pageRef]);

    const cardOptions = useMemo(() => ({
        ...options,
        style: {
            ...options.style,
            base: {
                ...themeStyle,
                ...options.style?.base
            }
        }
    }), [options, themeStyle]);

    const labelId = `${id}_label`;
    const resolvedErrorMessage = typeof error === "string" ? error : errorMessage;
    const hasError = !!error;
    const errorId = hasError && resolvedErrorMessage ? `${id}_error` : undefined;

    return (
        <div
            ref={containerRef}
            className="flex flex-col space-y-1 w-full relative"
            role="group"
            aria-labelledby={labelId}
            aria-required={required ? "true" : undefined}
            aria-invalid={hasError ? "true" : undefined}
            aria-describedby={errorId}
        >
            <label id={labelId} htmlFor={id}>{label}</label>
            <CardElement
                id={id}
                name={name}
                options={cardOptions}
                onChange={onChange}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                className={classNames(
                    "StripeElement !text-lg !h-[38px] !pt-[8px] w-full rounded-theme",
                    hasError ? (focused ? "!border-error-500" : "!border-error-300") : null
                )}
            />
            {errorId ? (
                <p
                    id={errorId}
                    role="alert"
                    className={classNames("mt-1 mb-0 text-sm font-semibold text-error-500", errorClassName)}
                >
                    {resolvedErrorMessage}
                </p>
            ) : null}
        </div>
    );
}

WebsiteCardElement.propTypes = {
    error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    errorClassName: PropTypes.string,
    errorMessage: PropTypes.string,
    id: PropTypes.string,
    label: PropTypes.string,
    name: PropTypes.string,
    onChange: PropTypes.func,
    options: PropTypes.object,
    pageRef: PropTypes.shape({current: PropTypes.any}),
    required: PropTypes.bool
};
